///
/// EarthBrowser
///
/// Created by Matt Giger on December 13, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { DatasetType } from "@/types/DatasetElement"

/**
 * A parsed `target` URL parameter: either a dataset entity (sat-..., geoid-...)
 * or a raw coordinate target (coords@lat,lon@name) used for OGC features
 */
export type ParsedTarget =
	| { kind: 'entity', type: DatasetType, id: string }
	| { kind: 'coords', lat: number, lon: number, name: string }

const cTargetPrefixToDatasetType: Record<string, DatasetType> = {
	sat: 'satellite',
	quake: 'earthquake',
	volcano: 'volcano',
	geoid: 'location',
	city: 'location',	// legacy alias for 'geoid' (old shared URLs)
	buoy: 'buoy',
	webcam: 'webcam',
	wildfire: 'wildfire',
	storm: 'hurricane',
	alert: 'alert',
	radar: 'nexrad',
	pin: 'placemark',
}

/**
 * Build a coordinate target value for the `target` URL parameter.
 *
 * Spaces in the name encode as `+` (standard query-string form encoding)
 * instead of `%20` so shared URLs stay readable: "San+Francisco" rather
 * than "San%20Francisco". A dash would read even better but isn't
 * reversible - place names legitimately contain dashes (Winston-Salem).
 * Literal `+` in a name survives round-tripping because encodeURIComponent
 * turns it into %2B before the space substitution.
 */
export function buildCoordsTarget(lat: number, lon: number, name: string): string {
	const encodedName = encodeURIComponent(name).replace(/%20/g, '+')
	return `coords@${lat},${lon}@${encodedName}`
}

/**
 * Parse a `target` URL parameter value.
 * Examples: sat-1990-037B, quake-abc123, volcano-123456, geoid-5678910,
 * buoy-xyz789, alert-nws-4d1a9b6c07e2, coords@37.7,-122.4@San+Francisco.
 * Placemarks: pin-<uuid> frames the whole placemark; pin-<uuid>:<memberId>
 * frames one member (the suffix is parsed by PlacemarksLayer).
 */
export function parseTarget(target: string): ParsedTarget | null {
	const coordsMatch = /^coords@([^,]+),([^@]+)@(.+)$/.exec(target)
	if (coordsMatch) {
		const lat = parseFloat(coordsMatch[1])
		const lon = parseFloat(coordsMatch[2])
		if (isNaN(lat) || isNaN(lon)) {
			return null
		}
		// `+` means space (see buildCoordsTarget); legacy %20 links still decode
		const name = decodeURIComponent(coordsMatch[3].replace(/\+/g, '%20'))
		return { kind: 'coords', lat, lon, name }
	}

	const match = /^(sat|quake|volcano|geoid|city|buoy|webcam|wildfire|storm|alert|radar|pin)-(.+)$/.exec(target)
	if (!match) {
		return null
	}
	return { kind: 'entity', type: cTargetPrefixToDatasetType[match[1]], id: match[2] }
}

/**
 * Parse a story deep-link target: `story-<uuid>` auto-starts playback at
 * stop 1; `story-<uuid>:<n>` starts at stop n (1-based, clamped by the
 * player). Stories aren't dataset entities, so they don't go through
 * parseTarget - callers check this first.
 */
export function parseStoryTarget(target: string): { storyId: string; stopIndex: number } | null {
	const match = /^story-([^:]+)(?::(\d+))?$/.exec(target)
	if (!match) return null
	const stopNumber = match[2] ? parseInt(match[2], 10) : 1
	return { storyId: match[1], stopIndex: Math.max(0, stopNumber - 1) }
}

/**
 * Updates the browser URL with a target parameter to enable back/forward navigation
 * This pushes a new history state so users can navigate between focused entities
 *
 * Important: This function checks if the target is already in the URL to avoid
 * creating duplicate history entries when an entity is already focused.
 *
 * Note: This dispatches a custom event that React components can listen to in order
 * to update the URL through React Router, ensuring proper history management.
 *
 * @param targetType - The URL prefix (sat, quake, volcano, geoid, buoy)
 * @param id - The entity ID
 */
export function pushTargetToHistory(targetType: string, id: string): void {
	const targetValue = `${targetType}-${id}`
	const currentUrl = new URL(window.location.href)
	const currentTarget = currentUrl.searchParams.get('target')

	// Don't push duplicate history entries if we're already on this target
	if (currentTarget === targetValue) {
		return
	}

	// Dispatch a custom event that React components can listen to
	// This allows React Router to handle the navigation properly
	window.dispatchEvent(new CustomEvent('earthbrowser:navigate', {
		detail: { target: targetValue }
	}))
}

/**
 * Removes the target parameter from the URL (via the same navigate event that
 * pushTargetToHistory uses). Called when focus is released so the URL doesn't
 * keep claiming an entity is focused after the user has left it.
 * Pushes a history entry, so Back returns to the previously focused entity.
 */
export function clearTargetFromHistory(): void {
	const currentUrl = new URL(window.location.href)
	if (!currentUrl.searchParams.has('target')) {
		return
	}

	window.dispatchEvent(new CustomEvent('earthbrowser:navigate', {
		detail: { target: null }
	}))
}

